import { useState, useEffect } from "react";
import "../../assets/css/receiverdashboard/Receiversupport.css";
import ReceiverMasterLayout from "../../components/layout/ReceiverMasterLayout";

const MOCK_TICKETS = [
  {
    id: "TKT-001",
    subject: "Need medicines urgently",
    message: "My mother needs insulin. We cannot afford it.",
    type: "urgent",
    status: "Admin Reviewing",
    createdAt: "2025-04-14 09:22",
    priority: "High",
  },
  {
    id: "TKT-002",
    subject: "Request for food supplies",
    message: "Our family of 5 hasn't had food for 2 days.",
    type: "general",
    status: "Resolved",
    createdAt: "2025-04-12 14:10",
    priority: "Normal",
  },
];

// ─── Utility Helpers ─────────────────────────────────────────────────────────

function generateTicketId() {
  return "TKT-" + Math.floor(Math.random() * 9000 + 1000);
}

function nowTimestamp() {
  return new Date().toLocaleString("en-PK", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ─── Status Badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const map = {
    Sent: "rs-badge rs-badge-sent",
    "Admin Reviewing": "rs-badge rs-badge-reviewing",
    Resolved: "rs-badge rs-badge-resolved",
  };
  return <span className={map[status] || "rs-badge"}>{status}</span>;
}

// ─── Priority Badge ───────────────────────────────────────────────────────────

function PriorityBadge({ priority }) {
  return (
    <span
      className={
        priority === "High"
          ? "rs-priority rs-priority-high"
          : "rs-priority rs-priority-normal"
      }
    >
      {priority === "High" ? "🔴 HIGH PRIORITY" : "🟢 Normal"}
    </span>
  );
}

// ─── Alert Banner ─────────────────────────────────────────────────────────────

function AlertBanner({ show, onClose }) {
  if (!show) return null;
  return (
    <div className="rs-alert-banner">
      <div className="rs-alert-inner">
        <span className="rs-alert-icon">🚨</span>
        <div className="rs-alert-text">
          <strong>Urgent Alert Sent!</strong>
          <p>Admin has been notified immediately via real-time notification.</p>
        </div>
        <button className="rs-alert-close" onClick={onClose}>
          ✕
        </button>
      </div>
    </div>
  );
}

// ─── Success Toast ────────────────────────────────────────────────────────────

function Toast({ message, show }) {
  if (!show) return null;
  return (
    <div className="rs-toast">
      <span>✅</span> {message}
    </div>
  );
}

// ─── Urgent Request Form ──────────────────────────────────────────────────────

function UrgentForm({ onSubmit }) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    bloodGroup: "",
    medicineDetails: "",
    location: "",
    requestType: "blood",
  });

  const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.location) return;
    onSubmit({
      id: generateTicketId(),
      subject: `URGENT: ${form.requestType === "blood" ? "Blood Required – " + form.bloodGroup : "Medicine Required"}`,
      message: `Patient: ${form.name} | Phone: ${form.phone} | Location: ${form.location} | Details: ${form.medicineDetails || form.bloodGroup}`,
      type: "urgent",
      status: "Sent",
      createdAt: nowTimestamp(),
      priority: "High",
    });
  };

  return (
    <div className="rs-form-card rs-form-urgent">
      <div className="rs-form-header rs-form-header-urgent">
        <span className="rs-form-icon">🚨</span>
        <div>
          <h2 className="rs-form-title">Urgent Request</h2>
          <p className="rs-form-subtitle">Blood / Medicine Emergency</p>
        </div>
        <span className="rs-pulse-dot"></span>
      </div>

      <form onSubmit={handleSubmit} className="rs-form-body">
        {/* Request Type */}
        <div className="rs-field-group">
          <label className="rs-label">What do you need?</label>
          <div className="rs-toggle-group">
            {["blood", "medicine"].map((t) => (
              <button
                key={t}
                type="button"
                className={`rs-toggle-btn ${form.requestType === t ? "rs-toggle-active" : ""}`}
                onClick={() => setForm({ ...form, requestType: t })}
              >
                {t === "blood" ? "🩸 Blood" : "💊 Medicine"}
              </button>
            ))}
          </div>
        </div>

        <div className="row g-3">
          <div className="col-md-6">
            <div className="rs-field-group">
              <label className="rs-label">Full Name *</label>
              <input
                className="rs-input"
                name="name"
                placeholder="Patient's full name"
                value={form.name}
                onChange={handle}
                required
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="rs-field-group">
              <label className="rs-label">Phone Number *</label>
              <input
                className="rs-input"
                name="phone"
                placeholder="+92 300 0000000"
                value={form.phone}
                onChange={handle}
                required
              />
            </div>
          </div>
        </div>

        {form.requestType === "blood" ? (
          <div className="rs-field-group">
            <label className="rs-label">Blood Group Required *</label>
            <div className="rs-blood-group-grid">
              {["A+", "A−", "B+", "B−", "AB+", "AB−", "O+", "O−"].map((bg) => (
                <button
                  key={bg}
                  type="button"
                  className={`rs-blood-btn ${form.bloodGroup === bg ? "rs-blood-active" : ""}`}
                  onClick={() => setForm({ ...form, bloodGroup: bg })}
                >
                  {bg}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="rs-field-group">
            <label className="rs-label">Medicine Name & Quantity *</label>
            <input
              className="rs-input"
              name="medicineDetails"
              placeholder="e.g. Insulin 10 units, Panadol 20 tablets"
              value={form.medicineDetails}
              onChange={handle}
            />
          </div>
        )}

        <div className="rs-field-group">
          <label className="rs-label">Your Location *</label>
          <input
            className="rs-input"
            name="location"
            placeholder="City, Area, Hospital name"
            value={form.location}
            onChange={handle}
            required
          />
        </div>

        <button type="submit" className="rs-btn rs-btn-urgent">
          <span>🚨</span> Send Urgent Alert to Admin
        </button>

        <p className="rs-form-note">
          ⚡ This will immediately notify the Admin with HIGH priority status.
        </p>
      </form>
    </div>
  );
}

// ─── General Support Ticket Form ──────────────────────────────────────────────

function SupportForm({ onSubmit }) {
  const [form, setForm] = useState({
    name: "",
    subject: "",
    category: "general",
    message: "",
  });

  const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.subject || !form.message) return;
    onSubmit({
      id: generateTicketId(),
      subject: form.subject,
      message: `From: ${form.name} | Category: ${form.category} | ${form.message}`,
      type: "general",
      status: "Sent",
      createdAt: nowTimestamp(),
      priority: "Normal",
    });
  };

  const categories = [
    { value: "general", label: "📋 General Help" },
    { value: "donation", label: "🎁 Donation Query" },
    { value: "account", label: "👤 Account Issue" },
    { value: "complaint", label: "⚠️ Complaint" },
    { value: "feedback", label: "💬 Feedback" },
  ];

  return (
    <div className="rs-form-card rs-form-support">
      <div className="rs-form-header rs-form-header-support">
        <span className="rs-form-icon">💬</span>
        <div>
          <h2 className="rs-form-title">Support Ticket</h2>
          <p className="rs-form-subtitle">Message Admin Directly</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="rs-form-body">
        <div className="row g-3">
          <div className="col-md-6">
            <div className="rs-field-group">
              <label className="rs-label">Your Name *</label>
              <input
                className="rs-input"
                name="name"
                placeholder="Your full name"
                value={form.name}
                onChange={handle}
                required
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="rs-field-group">
              <label className="rs-label">Category</label>
              <select
                className="rs-input rs-select"
                name="category"
                value={form.category}
                onChange={handle}
              >
                {categories.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="rs-field-group">
          <label className="rs-label">Subject *</label>
          <input
            className="rs-input"
            name="subject"
            placeholder="Brief subject of your message"
            value={form.subject}
            onChange={handle}
            required
          />
        </div>

        <div className="rs-field-group">
          <label className="rs-label">Your Message *</label>
          <textarea
            className="rs-input rs-textarea"
            name="message"
            rows={5}
            placeholder="Describe your issue or request in detail..."
            value={form.message}
            onChange={handle}
            required
          />
        </div>

        <button type="submit" className="rs-btn rs-btn-support">
          <span>📨</span> Send Message to Admin
        </button>
      </form>
    </div>
  );
}

// ─── Ticket Card ──────────────────────────────────────────────────────────────

function TicketCard({ ticket }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className={`rs-ticket-card ${ticket.priority === "High" ? "rs-ticket-urgent" : ""}`}
    >
      <div className="rs-ticket-header" onClick={() => setExpanded(!expanded)}>
        <div className="rs-ticket-left">
          <span className="rs-ticket-id">{ticket.id}</span>
          <div>
            <p className="rs-ticket-subject">{ticket.subject}</p>
            <small className="rs-ticket-date">🕐 {ticket.createdAt}</small>
          </div>
        </div>
        <div className="rs-ticket-right">
          <PriorityBadge priority={ticket.priority} />
          <StatusBadge status={ticket.status} />
          <span className="rs-expand-icon">{expanded ? "▲" : "▼"}</span>
        </div>
      </div>

      {expanded && (
        <div className="rs-ticket-body">
          <p className="rs-ticket-message">{ticket.message}</p>

          <div className="rs-ticket-timeline">
            <div className="rs-timeline-step rs-step-done">
              <span className="rs-step-dot"></span>
              <span>Ticket Submitted</span>
            </div>
            <div
              className={`rs-timeline-step ${ticket.status === "Admin Reviewing" || ticket.status === "Resolved" ? "rs-step-done" : "rs-step-pending"}`}
            >
              <span className="rs-step-dot"></span>
              <span>Admin Reviewing</span>
            </div>
            <div
              className={`rs-timeline-step ${ticket.status === "Resolved" ? "rs-step-done" : "rs-step-pending"}`}
            >
              <span className="rs-step-dot"></span>
              <span>Resolved</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── My Tickets Panel ─────────────────────────────────────────────────────────

function MyTickets({ tickets }) {
  const [filter, setFilter] = useState("All");
  const statuses = ["All", "Sent", "Admin Reviewing", "Resolved"];

  const filtered =
    filter === "All" ? tickets : tickets.filter((t) => t.status === filter);

  return (
    <div className="rs-panel">
      <div className="rs-panel-header">
        <h3 className="rs-panel-title">📋 My Support Tickets</h3>
        <span className="rs-ticket-count">{tickets.length} Total</span>
      </div>

      <div className="rs-filter-tabs">
        {statuses.map((s) => (
          <button
            key={s}
            className={`rs-filter-tab ${filter === s ? "rs-filter-active" : ""}`}
            onClick={() => setFilter(s)}
          >
            {s}
            <span className="rs-filter-count">
              {s === "All"
                ? tickets.length
                : tickets.filter((t) => t.status === s).length}
            </span>
          </button>
        ))}
      </div>

      <div className="rs-tickets-list">
        {filtered.length === 0 ? (
          <div className="rs-empty-state">
            <span className="rs-empty-icon">📭</span>
            <p>No tickets found</p>
          </div>
        ) : (
          filtered.map((t) => <TicketCard key={t.id} ticket={t} />)
        )}
      </div>
    </div>
  );
}

// ─── FAQ Section ──────────────────────────────────────────────────────────────

function FAQ() {
  const [open, setOpen] = useState(null);
  const faqs = [
    {
      q: "How long does it take for Admin to respond?",
      a: "Urgent requests are responded to within 1-2 hours. General tickets are handled within 24 hours.",
    },
    {
      q: "Can I submit multiple urgent requests?",
      a: "Yes, but please only submit urgent requests for genuine emergencies. False requests may delay help for others.",
    },
    {
      q: "What happens after I submit an urgent blood request?",
      a: "Admin receives an immediate real-time notification and will coordinate with available NGO donors.",
    },
    {
      q: "How will I know my ticket is resolved?",
      a: "Your ticket status will update to 'Resolved' in the My Tickets section. Admin may also contact you directly.",
    },
  ];

  return (
    <div className="rs-panel rs-faq-panel">
      <div className="rs-panel-header">
        <h3 className="rs-panel-title">❓ Frequently Asked Questions</h3>
      </div>
      <div className="rs-faq-list">
        {faqs.map((f, i) => (
          <div key={i} className="rs-faq-item">
            <button
              className="rs-faq-question"
              onClick={() => setOpen(open === i ? null : i)}
            >
              <span>{f.q}</span>
              <span>{open === i ? "▲" : "▼"}</span>
            </button>
            {open === i && <p className="rs-faq-answer">{f.a}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Stats Bar ────────────────────────────────────────────────────────────────

function StatsBar({ tickets }) {
  const sent = tickets.filter((t) => t.status === "Sent").length;
  const reviewing = tickets.filter(
    (t) => t.status === "Admin Reviewing",
  ).length;
  const resolved = tickets.filter((t) => t.status === "Resolved").length;
  const urgent = tickets.filter((t) => t.priority === "High").length;

  const stats = [
    {
      label: "Submitted",
      value: tickets.length,
      icon: "📋",
      cls: "rs-stat-total",
    },
    { label: "Awaiting", value: sent, icon: "📤", cls: "rs-stat-sent" },
    { label: "In Review", value: reviewing, icon: "🔍", cls: "rs-stat-review" },
    { label: "Resolved", value: resolved, icon: "✅", cls: "rs-stat-resolved" },
    { label: "Urgent", value: urgent, icon: "🚨", cls: "rs-stat-urgent" },
  ];

  return (
    <div className="rs-stats-bar">
      {stats.map((s, i) => (
        <div key={i} className={`rs-stat-card ${s.cls}`}>
          <span className="rs-stat-icon">{s.icon}</span>
          <span className="rs-stat-value">{s.value}</span>
          <span className="rs-stat-label">{s.label}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Main App ─────────────────────────────────────────────────────────────────

const ReceiverSupport = () => {
  const [activeTab, setActiveTab] = useState("urgent");
  const [tickets, setTickets] = useState(MOCK_TICKETS);
  const [showAlert, setShowAlert] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "" });

  const showToast = (msg) => {
    setToast({ show: true, message: msg });
    setTimeout(() => setToast({ show: false, message: "" }), 4000);
  };

  const handleUrgentSubmit = (ticket) => {
    setTickets((prev) => [ticket, ...prev]);
    setShowAlert(true);
    showToast("Urgent alert sent! Admin notified immediately.");
    setActiveTab("tickets");
  };

  const handleSupportSubmit = (ticket) => {
    setTickets((prev) => [ticket, ...prev]);
    showToast("Support ticket submitted successfully!");
    setActiveTab("tickets");
  };

  const tabs = [
    {
      key: "urgent",
      label: "🚨 Urgent Request",
      desc: "Blood / Medicine Emergency",
    },
    { key: "support", label: "💬 Support Ticket", desc: "Message Admin" },
    { key: "tickets", label: "📋 My Tickets", desc: "Track Status" },
    { key: "faq", label: "❓ FAQ", desc: "Common Questions" },
  ];

  return (
    <ReceiverMasterLayout>
      <div className="rs-root">
        {/* Header */}
        <header className="rs-header">
          <div className="rs-header-inner">
            <div className="rs-logo">
              <span className="rs-logo-icon">🤝</span>
              <div>
                <h1 className="rs-logo-title">DonateConnect</h1>
                <span className="rs-logo-sub">Receiver Support Center</span>
              </div>
            </div>
            <div className="rs-header-right">
              <div className="rs-online-badge">
                <span className="rs-online-dot"></span>
                Admin Online
              </div>
              <div className="rs-user-avatar">AR</div>
            </div>
          </div>
        </header>

        {/* Alert Banner */}
        <AlertBanner show={showAlert} onClose={() => setShowAlert(false)} />

        {/* Toast */}
        <Toast show={toast.show} message={toast.message} />

        <main className="rs-main">
          <div className="rs-container">
            {/* Hero */}
            <div className="rs-hero">
              <h2 className="rs-hero-title">How Can We Help You Today?</h2>
              <p className="rs-hero-sub">
                We're here to support you. For emergencies, use the Urgent
                Request button below.
              </p>
            </div>

            {/* Stats */}
            <StatsBar tickets={tickets} />

            {/* Tab Navigation */}
            <nav className="rs-tabs">
              {tabs.map((t) => (
                <button
                  key={t.key}
                  className={`rs-tab ${activeTab === t.key ? "rs-tab-active" : ""} ${t.key === "urgent" ? "rs-tab-urgent-btn" : ""}`}
                  onClick={() => setActiveTab(t.key)}
                >
                  <span className="rs-tab-label">{t.label}</span>
                  <span className="rs-tab-desc">{t.desc}</span>
                </button>
              ))}
            </nav>

            {/* Tab Content */}
            <div className="rs-tab-content">
              {activeTab === "urgent" && (
                <UrgentForm onSubmit={handleUrgentSubmit} />
              )}
              {activeTab === "support" && (
                <SupportForm onSubmit={handleSupportSubmit} />
              )}
              {activeTab === "tickets" && <MyTickets tickets={tickets} />}
              {activeTab === "faq" && <FAQ />}
            </div>

            {/* Emergency Hotline */}
            <div className="rs-hotline">
              <span className="rs-hotline-icon">📞</span>
              <div>
                <strong>Emergency Helpline</strong>
                <p>
                  Call Admin directly:{" "}
                  <a href="tel:+923001234567" className="rs-hotline-num">
                    +92 300 123 4567
                  </a>
                </p>
              </div>
            </div>
          </div>
        </main>

        <footer className="rs-footer">
          <p>
            © 2025 DonateConnect Smart Donation Management System · All Rights
            Reserved
          </p>
        </footer>
      </div>
    </ReceiverMasterLayout>
  );
};
export default ReceiverSupport;
